/**
 * How each firm setup item reads in the Firm Setup card and the checklist,
 * and what clicking it says to the onboarding agent.
 */
import {
  Building2,
  FileText,
  FolderCog,
  ListChecks,
  PenLine,
  Receipt,
  ShieldCheck,
  Users,
  type LucideIcon
} from "lucide-react";

import type { FirmItemId, FirmSetupItem, OnboardingStepId } from "./types";

type ItemCopy = {
  icon: LucideIcon;
  description: string;
  action: string;
  prompt: string;
  /** The Settings section that holds the rest of this item (`SettingsModal`). */
  settingsSection?: string;
};

export const FIRM_ITEM_COPY: Record<FirmItemId, ItemCopy> = {
  firm_details: {
    icon: Building2,
    description: "Name, address, phone, fax and logo for G-28s and letterhead.",
    action: "Fill In",
    prompt: "Let's fill in the firm details."
  },
  firm_instructions: {
    icon: PenLine,
    description: "How the firm writes and works. Every agent reads it first.",
    action: "Write with Me",
    prompt: "Let's write the firm instructions together."
  },
  templates: {
    icon: FileText,
    description:
      "Support letters, cover letters and briefs agents learn your style from.",
    action: "Upload",
    prompt: "I'd like to add our templates and sample letters."
  },
  house_style: {
    icon: ListChecks,
    description: "How exhibits are labeled and titled in every packet.",
    action: "Choose",
    prompt: "Let's choose the exhibit house style."
  },
  case_preparation: {
    icon: FolderCog,
    description:
      "Folder structure, exhibit titles, what new packets include and Case Insights.",
    action: "Set Up",
    prompt: "Let's set up case preparation defaults.",
    settingsSection: "case-preparation"
  },
  approvals: {
    icon: ShieldCheck,
    description:
      "Whether agents ask before every change, or only for ones that can't be undone.",
    action: "Decide",
    prompt: "Let's decide how approvals and events work."
  },
  time_billing: {
    icon: Receipt,
    description:
      "Time tracking, rounding, review, fee arrangement and LEDES export.",
    action: "Set Up",
    prompt: "Let's set up time and billing."
  },
  permissions: {
    icon: Users,
    description:
      "Who besides owners and admins can edit the library and automations.",
    action: "Review",
    prompt: "Let's review permissions."
  }
};

export const firmItemCopy = (id: string): ItemCopy =>
  FIRM_ITEM_COPY[id as FirmItemId] ?? {
    icon: Building2,
    description: "",
    action: "Open",
    prompt: `Let's set up ${id.replace(/_/g, " ")}.`
  };

/** What the checklist says when a member clicks a personal step. */
export const STEP_PROMPTS: Record<OnboardingStepId, string> = {
  welcome: "Let's get started.",
  connect: "Let's connect my email and calendar.",
  profile: "Let's finish my profile.",
  pin: "Let's pin the clients I work on.",
  agents: "Show me the firm's agents.",
  preferences: "Let's set my preferences."
};

/** Items this member still has open: what the checklist lists under Firm Setup. */
export const openFirmItems = (items: readonly FirmSetupItem[]) =>
  items.filter((i) => !i.done && !i.skipped);

/** Overlay the live progress on a card's snapshot (an item finished since). */
export const withLiveItems = (
  snapshot: readonly FirmSetupItem[],
  live: readonly FirmSetupItem[] | null | undefined
): FirmSetupItem[] => {
  if (!live?.length) return [...snapshot];
  const byId = new Map(live.map((i) => [i.id, i]));
  return snapshot.map((i) => {
    const now = byId.get(i.id);
    // A member's rows drop out of the live list once they are done.
    if (!now) return { ...i, done: true };
    return { ...i, done: now.done, skipped: now.skipped };
  });
};
